/**
 * MACRO #32 — GenesisBadge.
 *
 * Qualifies the TERRITORY itself as a Founding (Genesis) Territory. The label
 * is the canonical "GENESIS #N" once the set is FINALIZED, or a clearly
 * PROVISIONAL / PENDING state while the set is in DRAFT (a draft badge must
 * never present a definitive historical number). It NEVER attributes founder
 * status to the current owner.
 */
import type { GenesisTerritory } from "../../lib/genesis-api";

function FoundingStar({ className = "" }: { className?: string }) {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden className={className}>
      <path
        d="M6 1l1.4 2.85L10.5 4.3 8.25 6.5l.53 3.1L6 8.14 3.22 9.6l.53-3.1L1.5 4.3l3.1-.45L6 1z"
        fill="currentColor"
      />
    </svg>
  );
}

export function GenesisBadge({
  member,
  size = "md",
  testId = "genesis-badge",
}: {
  member: GenesisTerritory | null | undefined;
  size?: "sm" | "md";
  testId?: string;
}) {
  if (!member || !member.is_genesis) return null;

  const finalized = member.set_status === "FINALIZED" && member.genesis_ordinal != null;
  let label: string;
  if (finalized) {
    label = `GENESIS #${member.genesis_ordinal}`;
  } else if (member.preview_ordinal != null) {
    label = `GENESIS #${member.preview_ordinal} · PENDING`;
  } else {
    label = "GENESIS · PENDING";
  }

  const pad = size === "sm" ? "px-1.5 py-0.5 text-[8.5px]" : "px-2 py-1 text-[10px]";

  return (
    <span
      data-testid={testId}
      data-genesis-ordinal={member.genesis_ordinal ?? ""}
      data-genesis-status={member.set_status}
      title="Founding Territory — historical provenance of this Territory (not the current owner)"
      className={`inline-flex items-center gap-1 rounded font-mono uppercase tracking-[0.16em] border border-[#F5C451]/55 text-[#F5C451] bg-[#F5C451]/10 ${pad} ${
        finalized ? "" : "opacity-90"
      }`}
    >
      <FoundingStar />
      {label}
    </span>
  );
}
